import { useMutation, useQuery } from '@apollo/client'
import classNames from 'classnames'
import { FunctionComponent, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, NotificationType, TextArea } from '../../../components'
import { Star } from '../../../components/Star'
import { Avatar } from '../../../elements'
import { CREATE_RATING, GET_RATINGS } from '../../../gql'
import {
  Collaboration,
  CollaborationStatus,
  Pagination,
  Rating,
  RatingType,
} from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { useNotification } from '../../../utils/hooks'

interface GetBrandToCreatorRatingsData {
  getRatings: {
    items: Rating[]
    pagination: Pagination
  }
}

interface IDefaultProps {
  className?: string
  setReviewSubmitted?: any
}

interface IProps extends Partial<IDefaultProps> {
  collaboration: Collaboration
}

const defaultProps: IDefaultProps = {
  className: null,
}

export type CreateRatingFormData = {
  type: string
  stars: number
  review: string
  brandUuid: string
  creatorUuid: string
}

const WriteReview: FunctionComponent<IProps> = ({
  collaboration,
  className,
  setReviewSubmitted,
}) => {
  const { campaign, creator } = collaboration
  const { addNotification } = useNotification()
  const [starsRating, setStarsRating] = useState(null)
  const stars = [1, 2, 3, 4, 5]
  const [isLeaveReviewOpen, setIsLeaveReviewOpen] = useState(false)

  const { data, loading: getBrandToCreatorLoading } =
    useQuery<GetBrandToCreatorRatingsData>(GET_RATINGS, {
      variables: {
        options: {
          filters: {
            brandId: campaign.brand.id,
            creatorId: creator.id,
            type: RatingType.brandToCreator,
          },
        },
      },
    })

  const [createRating, { loading: createRatingLoading }] = useMutation(
    CREATE_RATING,
    {
      refetchQueries: [
        {
          query: GET_RATINGS,
          variables: {
            options: {
              filters: {
                brandId: campaign.brand.id,
                creatorId: creator.id,
                type: RatingType.brandToCreator,
              },
            },
          },
        },
        {
          query: GET_RATINGS,
          variables: {
            options: {
              filters: {
                creatorId: creator.id,
                type: RatingType.brandToCreator,
              },
              skip: 0,
              limit: 6,
            },
          },
        },
      ],
    },
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: RatingType.brandToCreator,
      stars: 0,
      review: '',
      brandUuid: collaboration.campaign.brand.uuid,
      creatorUuid: creator.uuid,
    },
  })

  const onSubmit = async (data: CreateRatingFormData) => {
    try {
      const { data: dataCreateRating } = await createRating({
        variables: {
          input: {
            type: RatingType.brandToCreator,
            stars: Number(data.stars),
            review: data.review,
            collaborationUuid: collaboration.uuid,
          },
        },
      })

      setReviewSubmitted(dataCreateRating.createRating)

      trackEvent(AnalyticsEvent.brandLeftCreatorReview, {
        collaborationId: collaboration.id,
        brandId: collaboration.campaign.brand.id,
        creatorId: collaboration.creator.id,
      })

      addNotification(`Review posted.`, NotificationType.success)
    } catch (error) {
      addNotification(
        `Something went wrong. Please try again.`,
        NotificationType.error,
      )
    }
  }

  // do not show leave a review button if brand already rated creator
  const isBrandToCreatorRating = data?.getRatings?.items[0]

  // do not show leave a review button if collaboration status not
  const isBrandAllowedToLeaveReview =
    collaboration.status === CollaborationStatus.contentAccepted

  if (
    getBrandToCreatorLoading ||
    !isBrandAllowedToLeaveReview ||
    isBrandToCreatorRating
  ) {
    return null
  }

  return (
    <div
      className={classNames(
        'bg-white shadow overflow-hidden sm:rounded-lg p-4',
        className,
      )}
    >
      <h3 className='text-base font-semibold leading-6 text-gray-900'>
        Rate your collaboration
      </h3>
      <p className='mt-1 text-sm font-normal leading-5 text-gray-500 font-sans'>
        Leave a review for {collaboration.creator?.firstName}.
      </p>
      {!isLeaveReviewOpen && (
        <Button
          title={`Write a public review`}
          loading={createRatingLoading}
          onClick={() => setIsLeaveReviewOpen(true)}
          className='mt-6 w-full sm:min-w-[250px] sm:max-w-[250px]'
        />
      )}

      {isLeaveReviewOpen && (
        <div className='mt-6'>
          <Avatar
            type='user'
            src={collaboration.creator.avatar?.url}
            size='md'
            alt={collaboration.creator?.firstName}
          />

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='mt-2 flex flex-row cursor-pointer'>
              {Object.values(stars).map((star, index) => {
                return (
                  <Star
                    key={star + 1}
                    value={index + 1}
                    name='star'
                    changeStarsRating={(index) => setStarsRating(index)}
                    isActiveStar={starsRating > index ? true : false}
                    className='cursor-pointer'
                    {...register('stars', {
                      required: `Please rate this creator.`,
                    })}
                  />
                )
              })}
            </div>

            <div className='mt-2'>
              {errors.stars?.message ? (
                <div
                  className=''
                  style={{ color: '#f7685b', fontSize: '0.875rem' }}
                >
                  {errors.stars?.message}
                </div>
              ) : null}
            </div>

            <div className='mt-2'>
              <TextArea
                placeholder={`Rate working with ${creator?.firstName}`}
                name='review'
                {...register('review', {
                  maxLength: {
                    value: 10000,
                    message: `Please keep your review under 10,000 characters.`,
                  },
                })}
              />
            </div>

            <div className='mt-2'>
              <Button
                title={'Submit'}
                className='w-full sm:min-w-[250px] sm:max-w-[250px]'
                loading={createRatingLoading}
              />
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

WriteReview.defaultProps = defaultProps

export { WriteReview }
